import { useRef } from 'react';

// ----------------------------------------------------------------------

const MAX_COMPARE_DEPTH = 8;

// Only object literals are safe to walk. A Date/File/Map/class instance has no own enumerable
// keys (or has exotic ones), and a React element's `_owner` is a fiber with cyclic pointers -
// recursing into either would either report a false match or blow the stack.
function isPlainObject(value) {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

// Deep value compare, used ONLY to decide whether `slides` really changed. It must never throw and
// must never report a false match: anything it cannot compare confidently is reported as CHANGED,
// which just falls back to the pre-fix identity churn.
export function isSameContent(a, b, depth = 0) {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return Number.isNaN(a) && Number.isNaN(b);
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (depth >= MAX_COMPARE_DEPTH) return false;
  if (a.$$typeof || b.$$typeof) return false; // React elements (yarl allows ReactNode captions)

  const aIsArray = Array.isArray(a);
  if (aIsArray !== Array.isArray(b)) return false;
  if (aIsArray) {
    return a.length === b.length && a.every((item, i) => isSameContent(item, b[i], depth + 1));
  }

  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(b, key) && isSameContent(a[key], b[key], depth + 1)
  );
}

// yet-another-react-lightbox resets the visible slide back to the `index` prop every time the
// `slides` prop changes IDENTITY (LightboxStateProvider dispatches "update" on [slides, index]).
// Call sites build slides inline - `images.map(...)` - so every re-render of the host component
// handed it a brand new array and snapped the viewer back to the slide it was opened on. Freeze
// the identity while the content is unchanged.
export function useStableSlides(slides) {
  const stable = useRef(slides);
  if (!isSameContent(stable.current, slides)) {
    stable.current = slides;
  }
  return stable.current;
}
